import {
  Avatar,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { maxPriceDecimalDigits, maxPriceDigits } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useIsDesktop } from 'Hooks';
import { PurpleX } from 'Images';
import { requiredMessage } from 'Regex';
import { useState, useEffect } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form/dist/types';

export const PriceInput = (
  props: TextFieldProps & {
    label: string;
    register: UseFormRegisterReturn<string>;
    errors?: { message: string | JSX.Element };
    shrink: boolean;
    value: number | string | null;
    helperText?: string;
  }
) => {
  const isDesktop = useIsDesktop();
  const [formattedValue, setFormattedValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (props.value !== null && !isEditing) {
      setFormattedValue(
        props.value.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })
      );
    }
  }, [props.value, isEditing]);

  const getInputWidth = () => {
    const length = formattedValue.length || 0;
    return length > 0 ? `${length + 1 + Math.floor(length / 3) * 2}ch` : '1ch';
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;

    if (input === '0') return;

    let validInput = input.replace(/[^\d.-]/g, '').replace(/(\..*)\./g, '$1');

    const parts = validInput.split('.');
    parts[0] = parts[0].slice(0, maxPriceDigits);
    if (parts.length > 1) {
      parts[1] = parts[1].slice(0, maxPriceDecimalDigits);
    }
    validInput = parts.join('.');

    setFormattedValue(validInput);

    const numericValue = parseFloat(validInput);

    if (!isNaN(numericValue)) {
      props.register.onChange({
        target: {
          name: props.register.name,
          value: numericValue,
        },
      } as unknown as React.ChangeEvent<HTMLInputElement>);
    } else {
      props.register.onChange({
        target: {
          name: props.register.name,
          value: '',
        },
      } as React.ChangeEvent<HTMLInputElement>);
    }
  };

  const handleFocus = () => {
    setIsEditing(true);
    setFormattedValue(props.value?.toString() || '');
  };

  const handleBlur = () => {
    setIsEditing(false);
    if (props.value !== null) {
      setFormattedValue(
        props.value.toLocaleString(undefined, {
          minimumFractionDigits: 0,
          maximumFractionDigits: maxPriceDecimalDigits,
          maximumSignificantDigits: maxPriceDigits,
        })
      );
    } else {
      setFormattedValue('');
    }
  };

  return (
    <TextField
      type='text'
      label={props.label}
      error={props.errors?.message !== undefined}
      FormHelperTextProps={{ sx: { fontSize: '14px', color: '#1552E8' } }}
      helperText={props.errors?.message || props.helperText}
      variant='outlined'
      inputMode='decimal'
      value={formattedValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      sx={{
        width: isDesktop ? '400px' : '100%',
        borderRadius: '6px',
        '& .MuiOutlinedInput-notchedOutline': {
          borderRadius: '6px',
          border: '1px solid rgba(23, 82, 175, 0.2)',
          borderWidth: '1px !important',
          borderColor: 'primary.shade_200',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderRadius: '6px',
          border: '1px solid rgba(23, 82, 175, 0.2)',
          borderWidth: '1px !important',
          borderColor: 'primary.shade_200',
        },
        '& .MuiInputLabel-root[data-shrink="false"]': {
          mt: isDesktop ? '-5px' : '-8px',
        },
        '& .MuiTypography-root': { fontWeight: '400' },
        '.MuiInputBase-inputAdornedEnd': {
          textAlign: 'right',
        },
        '& .MuiOutlinedInput-input': {
          width: getInputWidth(),
        },
        ...props.sx,
      }}
      InputProps={{
        endAdornment: formattedValue.length > 0 && (
          <Stack
            sx={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              m: '0 15px 0 0',
            }}
          >
            <InputAdornment position='end'>₪</InputAdornment>
            <InputAdornment position='end'>
              <IconButton
                onClick={() =>
                  handleChange({
                    target: { value: '' },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                sx={{ width: '20px', height: '20px' }}
              >
                <Avatar
                  alt={Dictionary[DICTIONARY.General].reset}
                  src={PurpleX}
                  sx={{ width: '20px', height: '20px' }}
                />
              </IconButton>
            </InputAdornment>
          </Stack>
        ),
        inputMode: 'decimal',
        style: {
          padding: '0px',
          height: isDesktop ? '48px' : '42px',
        },
        'aria-required': props.errors?.message === requiredMessage,
      }}
      InputLabelProps={{ shrink: props.shrink }}
    />
  );
};
